/**
 * Certification currency — GET /api/settings/certification-currency
 * (authenticated): the currency a new invoice or till sale is issued in
 * today, and the unit and wording every certification-currency period
 * states. A document is settled in its own stored currency; a code the
 * core tables do not know (a currency defined by its period) is settled
 * with the unit its period states.
 */

import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CURRENCY_MINOR_UNITS,
  CURRENCY_SWITCH_REFUSAL_CODE,
  currencyDefinitionOf,
  type CertificationCurrencyWire,
  type CurrencyRef,
  type StampDutyScaleWire,
} from '@efacture/core';
import { apiClient } from '@/api/client';

export const certificationCurrencyKeys = {
  all: ['settings', 'certification-currency'] as const,
  stampDutyScale: ['settings', 'stamp-duty-scale'] as const,
};

export function useCertificationCurrency() {
  return useQuery({
    queryKey: certificationCurrencyKeys.all,
    queryFn: () => apiClient.get<CertificationCurrencyWire>('/settings/certification-currency'),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * `code` as the money functions take it, or the certification currency of
 * the day when `code` is null. Before the periods are known, only a code
 * the core tables know resolves (to itself). Null when the currency cannot
 * be resolved: the periods are not loaded, or no period nor table defines
 * its payable unit.
 */
export function resolveCurrency(
  code: string | null,
  certification: CertificationCurrencyWire | undefined
): CurrencyRef | null {
  const resolved = code ?? certification?.currency ?? null;
  if (resolved === null) return null;
  if (certification === undefined) {
    return CURRENCY_MINOR_UNITS[resolved] === undefined ? null : resolved;
  }
  try {
    return currencyDefinitionOf(certification.periods, resolved);
  } catch {
    return null;
  }
}

export interface CurrencyResolver {
  /** See `resolveCurrency`. */
  resolve: (code: string | null) => CurrencyRef | null;
  /** True while the periods are still being fetched. */
  isPending: boolean;
  /**
   * True when they could not be read: the request failed, or no period
   * covers today (the API refuses to name a certification currency).
   */
  isError: boolean;
  /**
   * The API's refusal of the day's currency when a stored switch of the
   * certification currency covers today and cannot be applied; null otherwise.
   */
  refusal?: string | null;
}

/**
 * The message of `error` when it is an `Error` whose `code` is
 * CURRENCY_SWITCH_REFUSAL_CODE (the API's refusal of a day a currency switch
 * covers); else null.
 */
export function currencySwitchRefusal(error: unknown): string | null {
  return error instanceof Error && (error as { code?: unknown }).code === CURRENCY_SWITCH_REFUSAL_CODE
    ? error.message
    : null;
}

/** Resolves document currencies against the certification-currency periods. */
export function useCurrencyResolver(): CurrencyResolver {
  const { data, isPending, isError, error } = useCertificationCurrency();
  const resolve = useCallback((code: string | null) => resolveCurrency(code, data), [data]);
  return { resolve, isPending, isError, refusal: currencySwitchRefusal(error) };
}

/**
 * GET /api/settings/stamp-duty-scale: the stamp-duty scale (CGI art. 519)
 * the API applies to a settlement recorded today, in the certification
 * currency of the day. The request fails when no row is in force in that
 * currency; the API then refuses a cash settlement too.
 */
export function useStampDutyScale(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: certificationCurrencyKeys.stampDutyScale,
    queryFn: () => apiClient.get<StampDutyScaleWire>('/settings/stamp-duty-scale'),
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  });
}
